import React, { useState } from "react";
import axios from "axios";
import { X, Send, Loader2, CheckCircle2, Handshake } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

const TIPI_ATTIVITA = [
  { value: "azienda", label: "Azienda / SRL / SPA" },
  { value: "professionista", label: "Libero professionista / P.IVA" },
  { value: "ente_formazione", label: "Ente di formazione" },
  { value: "ong", label: "Associazione / ONG" },
  { value: "altro", label: "Altro" },
];

const EMPTY = {
  nome_azienda: "",
  referente: "",
  email: "",
  telefono: "",
  sito_web: "",
  tipo_attivita: "azienda",
  settore: "",
  messaggio: "",
  privacy_accettata: false,
  website: "", // honeypot
};

export default function CandidaturaPartnerModal({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const { toast } = useToast();

  const reset = () => {
    setForm(EMPTY);
    setSuccess(false);
    setSubmitting(false);
  };

  const close = () => {
    reset();
    onClose?.();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_accettata) {
      toast({
        description: "Devi accettare l'informativa privacy per procedere.",
        variant: "destructive",
      });
      return;
    }
    if (form.messaggio.trim().length < 10) {
      toast({
        description: "Scrivi un messaggio di almeno 10 caratteri.",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/partners/candidatura`, form);
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg).join(", ")
          : "Errore invio candidatura. Riprova più tardi.";
      toast({ description: msg, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="candidatura-partner-modal"
      className="fixed inset-0 z-[100] flex items-start md:items-center justify-center p-4 md:p-6 overflow-y-auto"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={close}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8 overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] px-6 py-5 md:px-8 md:py-6">
          <button
            onClick={close}
            data-testid="candidatura-close"
            className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            aria-label="Chiudi"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-[#f59e0b] text-[#0a1f55] flex items-center justify-center">
              <Handshake className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-[#fbbf24]">
                Diventa Partner ACDT
              </div>
              <h2 className="text-xl md:text-2xl font-extrabold text-white leading-tight">
                Entra nella nostra rete
              </h2>
            </div>
          </div>
          <p className="mt-3 text-slate-200/90 text-sm leading-relaxed">
            Compila il modulo e il team ACDT ti contatterà entro <strong>5 giorni lavorativi</strong>
            {" "}per esplorare insieme una partnership su digitale, tecnologia e sostenibilità.
          </p>
        </div>

        {/* Body */}
        {success ? (
          <div
            data-testid="candidatura-success"
            className="p-8 md:p-12 text-center"
          >
            <div className="h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h3 className="text-2xl font-extrabold text-[#0a1f55]">
              Candidatura ricevuta!
            </h3>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Grazie per il tuo interesse. Abbiamo inviato una conferma a{" "}
              <strong>{form.email}</strong>. Il nostro team ti contatterà entro 5 giorni lavorativi.
            </p>
            <Button
              onClick={close}
              data-testid="candidatura-success-close"
              className="mt-6 bg-[#0a1f55] hover:bg-[#102a6b] text-white font-bold h-11 px-6"
            >
              Chiudi
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 md:p-8 space-y-4">
            {/* Honeypot invisibile */}
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              className="absolute -left-[9999px] w-px h-px opacity-0"
            />

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nome_azienda" className="text-[#0a1f55] font-semibold">
                  Nome azienda *
                </Label>
                <Input
                  id="nome_azienda"
                  data-testid="candidatura-nome-azienda"
                  required
                  value={form.nome_azienda}
                  onChange={(e) => setForm({ ...form, nome_azienda: e.target.value })}
                  placeholder="Es. Acme SRL"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="referente" className="text-[#0a1f55] font-semibold">
                  Nome referente *
                </Label>
                <Input
                  id="referente"
                  data-testid="candidatura-referente"
                  required
                  value={form.referente}
                  onChange={(e) => setForm({ ...form, referente: e.target.value })}
                  placeholder="Mario Rossi"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email" className="text-[#0a1f55] font-semibold">
                  Email *
                </Label>
                <Input
                  id="email"
                  data-testid="candidatura-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="referente@azienda.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="telefono" className="text-[#0a1f55] font-semibold">
                  Telefono
                </Label>
                <Input
                  id="telefono"
                  data-testid="candidatura-telefono"
                  value={form.telefono}
                  onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                  placeholder="+39 333 1234567"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="tipo_attivita" className="text-[#0a1f55] font-semibold">
                  Tipo attività *
                </Label>
                <select
                  id="tipo_attivita"
                  data-testid="candidatura-tipo"
                  value={form.tipo_attivita}
                  onChange={(e) => setForm({ ...form, tipo_attivita: e.target.value })}
                  className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                >
                  {TIPI_ATTIVITA.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="settore" className="text-[#0a1f55] font-semibold">
                  Settore
                </Label>
                <Input
                  id="settore"
                  data-testid="candidatura-settore"
                  value={form.settore}
                  onChange={(e) => setForm({ ...form, settore: e.target.value })}
                  placeholder="Es. Software, Formazione, Consulenza"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="sito_web" className="text-[#0a1f55] font-semibold">
                Sito web
              </Label>
              <Input
                id="sito_web"
                data-testid="candidatura-sito"
                value={form.sito_web}
                onChange={(e) => setForm({ ...form, sito_web: e.target.value })}
                placeholder="https://www.azienda.com"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="messaggio" className="text-[#0a1f55] font-semibold">
                Messaggio * <span className="text-slate-500 font-normal">(min. 10 caratteri)</span>
              </Label>
              <Textarea
                id="messaggio"
                data-testid="candidatura-messaggio"
                required
                minLength={10}
                value={form.messaggio}
                onChange={(e) => setForm({ ...form, messaggio: e.target.value })}
                placeholder="Raccontaci brevemente chi siete, cosa fate e in che modo vorreste collaborare con ACDT..."
                className="mt-1 min-h-[120px]"
              />
            </div>

            <label className="flex items-start gap-3 pt-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="candidatura-privacy"
                checked={form.privacy_accettata}
                onChange={(e) => setForm({ ...form, privacy_accettata: e.target.checked })}
                className="mt-1 h-4 w-4"
                required
              />
              <span className="text-sm text-slate-600 leading-relaxed">
                Ho letto e accetto l&apos;
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#0a1f55] font-semibold underline"
                >
                  Informativa Privacy
                </a>{" "}
                e acconsento al trattamento dei dati per essere ricontattato/a. *
              </span>
            </label>

            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-4 border-t border-slate-100">
              <Button
                type="button"
                onClick={close}
                variant="outline"
                className="h-11"
                data-testid="candidatura-annulla"
              >
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="candidatura-submit"
                className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-bold h-11 px-6"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Invio in corso…
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Invia candidatura
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
